import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert, Box, Button, MenuItem, Paper, Radio, RadioGroup, FormControlLabel, Stack, Step, StepLabel, Stepper, TextField, Typography,
} from '@mui/material';
import { api, ApiError } from '../api/client';

const STEPS = ['정보 입력', '키 유형 선택', '완료'];

export function Signup() {
  const [step, setStep] = useState(0);
  const [f, setF] = useState({ loginId: '', pw: '', name: '', phone: '', email: '', countryCode: '410' });
  // 기본 선택을 두지 않는다 — 사용자가 키 관리 방식을 직접 고르게 한다(1차 확인 43·48번)
  const [keyType, setKeyType] = useState<'TYPE1' | 'TYPE2' | ''>('');
  const [err, setErr] = useState<string | null>(null);

  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });

  const submit = async (): Promise<void> => {
    setErr(null);
    try {
      await api.post('/auth/signup', { ...f, keyType });
      setStep(2);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '가입 실패');
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', bgcolor: '#0b1f3a', py: 4 }}>
      <Paper sx={{ p: 5, width: 520 }}>
        <Typography variant="h2" mb={3}>회원가입</Typography>
        <Stepper activeStep={step} sx={{ mb: 4 }}>
          {STEPS.map((s) => (
            <Step key={s}><StepLabel>{s}</StepLabel></Step>
          ))}
        </Stepper>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

        {step === 0 && (
          <Stack spacing={2}>
            <TextField label="아이디" value={f.loginId} onChange={set('loginId')} />
            <TextField label="비밀번호 (8자 이상)" type="password" value={f.pw} onChange={set('pw')} />
            <TextField label="이름" value={f.name} onChange={set('name')} />
            <TextField label="연락처" value={f.phone} onChange={set('phone')} />
            <TextField label="이메일" value={f.email} onChange={set('email')} />
            <TextField select label="국가코드" value={f.countryCode} onChange={set('countryCode')}>
              <MenuItem value="410">대한민국 (410)</MenuItem>
              <MenuItem value="840">미국 (840)</MenuItem>
            </TextField>
            <Button variant="contained" onClick={() => setStep(1)} disabled={!f.loginId || f.pw.length < 8 || !f.name}>
              다음
            </Button>
          </Stack>
        )}

        {step === 1 && (
          <Stack spacing={2}>
            <RadioGroup value={keyType} onChange={(e) => setKeyType(e.target.value as 'TYPE1' | 'TYPE2')}>
              <Paper sx={{ p: 2, mb: 1 }}>
                <FormControlLabel value="TYPE1" control={<Radio />} label="Type1 (직접 관리)" />
                <Typography variant="body2" color="text.secondary" sx={{ pl: 4 }}>
                  KeyStore를 직접 보관·서명합니다. 재발급 불가, 분실 시 복구 불가.
                </Typography>
              </Paper>
              <Paper sx={{ p: 2 }}>
                <FormControlLabel value="TYPE2" control={<Radio />} label="Type2 (위탁 관리)" />
                <Typography variant="body2" color="text.secondary" sx={{ pl: 4 }}>
                  서비스가 검증필 KMS로 개인키를 안전 보관·서명 대행합니다.
                </Typography>
              </Paper>
            </RadioGroup>
            {keyType && (
              <Alert severity="info">
                선택한 키 관리 방식: <b>{keyType === 'TYPE1' ? 'Type1 (직접 관리)' : 'Type2 (위탁 관리)'}</b>
              </Alert>
            )}
            <Stack direction="row" spacing={2}>
              <Button onClick={() => setStep(0)}>이전</Button>
              <Button variant="contained" onClick={submit} disabled={!keyType} sx={{ flexGrow: 1 }}>가입 신청</Button>
            </Stack>
          </Stack>
        )}

        {step === 2 && (
          <Stack spacing={2} alignItems="center">
            <Alert severity="success" sx={{ width: '100%' }}>
              가입 신청이 접수되었습니다. <b>관리자 승인 후</b> 로그인할 수 있습니다.
            </Alert>
            <Typography variant="body2" color="text.secondary">
              신청한 키 관리 방식: <b>{keyType === 'TYPE1' ? 'Type1 (직접 관리)' : 'Type2 (위탁 관리)'}</b>
            </Typography>
            <Button component={Link} to="/login" variant="contained">로그인으로</Button>
          </Stack>
        )}
      </Paper>
    </Box>
  );
}
